import { createPageShell } from '../ui/pageShell.js';
import { el, btn } from '../ui/dom.js';
import { nationalityFlagMarkup } from '../components/nationalityFlag.js';
import { openBoardDecisionModal, openCasinoBetModal } from './boardDecisionModal.js';
import { buildBoardDecisionCopy } from './boardDecisionCopy.js';
import { createBoardWarmupIntro, BOARD_WARMUP_SUBTITLE } from './boardWarmupIntro.js';
import { takeLastBoardState } from './boardStateCache.js';
import { fetchMeetingSession } from '../championship/api.js';
import { fetchCircuitById } from '../circuits/api.js';
import { fetchCarById } from '../cars/api.js';
import { fetchDriverById } from '../drivers/api.js';
import { buildCourseHash, parseBoardHashParams } from '../championship/seasonPrefs.js';
import { getClientClaimerId } from '../championship/seasonPrefs.js';
import {
  emitBoardAdminTakeControl,
  emitBoardCasinoBet,
  emitBoardDecision,
  emitBoardReclaim,
  emitBoardRoll,
  onBoardError,
  onBoardState,
  onBoardWarmupEnd,
} from '../championship/meetingSocket.js';
import { labelOwnDriver } from '../auth/ownDriverLabel.js';
import { getCurrentUser } from '../auth/session.js';
import {
  mountCircuit3dPreview,
  type CameraFollowMode,
  type Circuit3dPreviewHandle,
  type LivePlayer3D,
} from '../circuits/circuit3dPreview.js';
import { buildPlayerProfileTooltipHtml } from '../game/playerProfileTooltip.js';
import type { GridSlot } from '@multirx/shared/types/championship.js';
import type { BoardStatePayload } from '@multirx/shared/types/board.js';
import type { Car } from '@multirx/shared/types/car.js';
import type { Driver } from '@multirx/shared/types/driver.js';
import {
  effectiveBonusFromChampionshipStars,
  effectiveCarBonusFromChampionshipStars,
} from '@multirx/shared/game/starLadder.js';

function escapeHtml(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}

type BoardEntityCache = {
  driver: Driver | null;
  car: Car | null;
};

/** Charge modèles 3D + fiches pilote/voiture pour tooltips. */
async function loadBoardEntities(grid: GridSlot[]): Promise<{
  players: LivePlayer3D[];
  entities: Map<number, BoardEntityCache>;
}> {
  const entities = new Map<number, BoardEntityCache>();
  const players = await Promise.all(
    grid.map(async (slot): Promise<LivePlayer3D> => {
      let car: Car | null = null;
      let driver: Driver | null = null;
      try {
        car = await fetchCarById(slot.carId);
      } catch {
        /* ignore */
      }
      try {
        driver = await fetchDriverById(slot.driverId);
      } catch {
        /* ignore */
      }
      entities.set(slot.playerIndex, { driver, car });
      return {
        id: slot.playerIndex,
        carName: slot.carName,
        model3dPath: car?.model3dPath ?? null,
        position: {
          mainPathIndex: 0,
          jokerBranchIndex: null,
          startLaneIndex: null,
        },
      };
    })
  );
  return { players, entities };
}

function buildBoardCameraSwitch(onMode: (mode: CameraFollowMode) => void): {
  root: HTMLElement;
  setActive: (mode: CameraFollowMode) => void;
} {
  const root = el('div', {
    class: 'board-camera-modes',
    role: 'radiogroup',
    'aria-label': 'Mode caméra',
  });
  const modes: { mode: CameraFollowMode; label: string }[] = [
    { mode: 'me', label: 'Ma voiture' },
    { mode: 'active', label: 'Voiture active' },
    { mode: 'free', label: 'Libre' },
  ];
  const buttons = new Map<CameraFollowMode, HTMLButtonElement>();
  for (const { mode, label } of modes) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'board-camera-mode-btn';
    b.textContent = label;
    b.setAttribute('role', 'radio');
    b.addEventListener('click', () => onMode(mode));
    root.appendChild(b);
    buttons.set(mode, b);
  }
  const setActive = (mode: CameraFollowMode): void => {
    for (const [m, b] of buttons) {
      const on = m === mode;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-checked', on ? 'true' : 'false');
    }
  };
  setActive('me');
  return { root, setActive };
}

let disposeCurrent: (() => void) | null = null;

export function mountBoardView(root: HTMLElement): void {
  disposeCurrent?.();
  disposeCurrent = null;
  root.innerHTML = '';
  root.className = 'app-view circuits-app';

  const { meetingSession, driverId } = parseBoardHashParams();
  if (meetingSession == null || driverId == null) {
    root.innerHTML = '<p class="auth-error">Session de plateau invalide.</p>';
    return;
  }

  const { page, body } = createPageShell({
    title: 'Tour de chauffe',
    subtitle: BOARD_WARMUP_SUBTITLE,
    wide: true,
  });
  body.appendChild(createBoardWarmupIntro());
  const section = el('div');
  section.innerHTML = '<p>Chargement du plateau…</p>';
  body.appendChild(section);
  root.appendChild(page);

  const claimerId = getClientClaimerId();
  let preview: Circuit3dPreviewHandle | null = null;
  let grid: GridSlot[] = [];
  let currentState: BoardStatePayload | null = null;
  let decisionModalOpen = false;
  let entityCache = new Map<number, BoardEntityCache>();
  /** Slots IA dont CE client a pris le contrôle (admin). */
  const takenOverIndices = new Set<number>();

  const viewport = el('div', { class: 'board-viewport' });
  const cameraSwitch = buildBoardCameraSwitch((mode) => {
    preview?.setCameraMode?.(mode);
    cameraSwitch.setActive(mode);
  });
  const viewportWrap = el('div', { class: 'board-viewport-wrap' }, viewport, cameraSwitch.root);
  const statusLine = el('p', { class: 'board-status-text' });
  const currencyLine = el('p', { class: 'board-currency-line' });
  const rollBtnWrap = el('div', { class: 'board-roll-wrap' });
  const statusPanel = el(
    'div',
    { class: 'board-status-panel' },
    statusLine,
    currencyLine,
    rollBtnWrap
  );
  const turnStrip = el('div', { class: 'board-roster' });
  const sidePanel = el('div', { class: 'board-side' }, statusPanel, turnStrip);
  const boardBody = el('div', { class: 'board-view-body' }, viewportWrap, sidePanel);

  const teardownListeners: Array<() => void> = [];

  function driverLabel(playerIndex: number): string {
    const slot = grid[playerIndex];
    const raw = slot?.driverName ?? `Pilote ${playerIndex + 1}`;
    return labelOwnDriver(raw, slot?.driverId);
  }

  function myPlayerIndex(): number {
    return grid.findIndex((s) => s.driverId === driverId);
  }

  const canTakeControl = getCurrentUser()?.role === 'admin';

  function boardTooltipHtml(playerIndex: number): string {
    const slot = grid[playerIndex];
    const cached = entityCache.get(playerIndex);
    const driver = cached?.driver;
    const car = cached?.car;
    const isMe = slot?.driverId === driverId;
    const isCurrent = currentState?.currentPlayerIndex === playerIndex;
    const done = currentState?.playerDone[playerIndex] ?? false;
    const statusLabel = done ? 'TERMINÉ' : isCurrent ? 'EN JEU' : 'EN ATTENTE';
    const statusVariant = done ? 'finished' : isCurrent ? 'racing' : 'waiting';
    const pilotage = driver?.pilotage ?? 1;
    const defense = driver?.defense ?? 1;
    const engine = car?.engine ?? 1;
    const tire = car?.tire ?? 1;
    return buildPlayerProfileTooltipHtml({
      driverName: slot?.driverName ?? `Pilote ${playerIndex + 1}`,
      carName: slot?.carName ?? null,
      driverImagePath: driver?.imagePath ?? null,
      driverDivision: driver?.division ?? car?.division ?? null,
      nationalityCode: slot?.nationality ?? driver?.nationality,
      isOwnDriver: isMe,
      rank: playerIndex + 1,
      statusLabel,
      statusVariant,
      handle: isMe
        ? 'VOUS'
        : car?.division != null
          ? `DIVISION D${car.division}`
          : 'TOUR DE CHAUFFE',
      bio: null,
      stats: [
        { value: `+${effectiveCarBonusFromChampionshipStars(engine)}`, label: 'Ligne' },
        { value: `+${effectiveBonusFromChampionshipStars(pilotage)}`, label: 'Virage' },
        { value: `+${effectiveCarBonusFromChampionshipStars(tire)}`, label: 'Départ' },
        { value: `+${effectiveBonusFromChampionshipStars(defense)}`, label: 'Défense' },
      ],
      footer: slot?.carName ? slot.carName.toUpperCase() : null,
    });
  }

  function renderHud(): void {
    if (!currentState) return;
    const mine = myPlayerIndex();
    turnStrip.innerHTML = '';
    currentState.order.forEach((playerIndex) => {
      const isCurrent = currentState!.currentPlayerIndex === playerIndex && !currentState!.complete;
      const isDone = currentState!.playerDone[playerIndex];
      const isHuman = currentState!.playerIsHuman[playerIndex];
      const slot = grid[playerIndex];
      const row = el('div', {
        class: `board-roster-row${isCurrent ? ' is-current' : ''}${isDone ? ' is-done' : ''}`,
      });

      const main = el('div', { class: 'board-roster-row-main' });
      const nameEl = el('span', { class: 'board-roster-name' });
      const flag = slot?.nationality
        ? `${nationalityFlagMarkup(slot.nationality, { className: 'board-roster-flag' })} `
        : '';
      nameEl.innerHTML = `${flag}${escapeHtml(driverLabel(playerIndex))}`;
      main.appendChild(nameEl);
      // Points de course : uniquement les siens
      if (playerIndex === mine) {
        main.appendChild(
          el('span', {
            class: 'board-roster-points',
            text: `${currentState!.currency[playerIndex]} pts de course`,
          })
        );
      }
      row.appendChild(main);

      const side = el('div', { class: 'board-roster-side' });
      if (isDone) {
        side.appendChild(el('span', { class: 'board-roster-done-badge', text: 'Terminé' }));
      } else if (isCurrent) {
        side.appendChild(el('span', { class: 'board-roster-turn-badge', text: 'En jeu' }));
      }
      if (canTakeControl && !isHuman && !isDone) {
        side.appendChild(
          btn('🎮 Prendre le contrôle', { class: 'btn-sm' }, () => {
            takenOverIndices.add(playerIndex);
            emitBoardAdminTakeControl(currentState!.gameId, playerIndex);
          })
        );
      }
      row.appendChild(side);
      turnStrip.appendChild(row);
    });

    if (mine >= 0) {
      const pts = currentState.currency[mine] ?? 0;
      currencyLine.textContent = `Vos points de course : ${pts}`;
      currencyLine.hidden = false;
    } else {
      currencyLine.hidden = true;
    }

    rollBtnWrap.innerHTML = '';
    const currentIsMine =
      currentState.currentPlayerIndex === mine ||
      (currentState.currentPlayerIndex != null &&
        takenOverIndices.has(currentState.currentPlayerIndex));
    const isMyTurn = currentIsMine && !currentState.complete;
    if (isMyTurn && !currentState.pendingDecision) {
      rollBtnWrap.appendChild(
        btn('Lancer le dé', { variant: 'primary' }, () => {
          emitBoardRoll();
        })
      );
    }
    statusLine.textContent = currentState.complete
      ? 'Tour de chauffe terminé — la course démarre…'
      : isMyTurn && currentState.pendingDecision
        ? 'Décision en cours…'
        : isMyTurn
          ? 'À vous de jouer.'
          : `Au tour de ${driverLabel(currentState.currentPlayerIndex ?? -1)}.`;

    maybeShowDecisionModal(mine);
  }

  function maybeShowDecisionModal(myIdx: number): void {
    if (decisionModalOpen || !currentState?.pendingDecision) return;
    const decisionPlayerIndex = currentState.pendingDecision.playerIndex;
    const isMine = decisionPlayerIndex === myIdx || takenOverIndices.has(decisionPlayerIndex);
    if (!isMine) return;
    decisionModalOpen = true;
    const outcome = currentState.pendingDecision.outcome;

    if (outcome.type === 'casino') {
      const maxStake = currentState.currency[decisionPlayerIndex] ?? 0;
      void openCasinoBetModal(document.body, maxStake, 5).then(({ betNumber, stake }) => {
        decisionModalOpen = false;
        emitBoardCasinoBet(betNumber, stake);
      });
      return;
    }

    const copy = buildBoardDecisionCopy(outcome);
    if (!copy) {
      decisionModalOpen = false;
      emitBoardDecision(false);
      return;
    }
    void openBoardDecisionModal(document.body, copy).then((accepted) => {
      decisionModalOpen = false;
      emitBoardDecision(accepted);
    });
  }

  fetchMeetingSession(meetingSession, claimerId)
    .then(async (sessionData) => {
      grid = sessionData.session.grid;
      const saved = await fetchCircuitById(sessionData.session.circuitId);
      section.innerHTML = '';
      section.appendChild(boardBody);

      const initial = takeLastBoardState(meetingSession);
      currentState = initial;

      const loaded = await loadBoardEntities(grid);
      entityCache = loaded.entities;
      const players = loaded.players.map((p) => ({
        ...p,
        position: {
          mainPathIndex: initial?.playerPathIndices[p.id] ?? 0,
          jokerBranchIndex: null as number | null,
          startLaneIndex: initial?.playerStartLaneIndex?.[p.id] ?? null,
        },
      }));

      preview = mountCircuit3dPreview(viewport, saved.circuit, players, {
        boardLayout: initial?.layout,
        interactive: true,
        circuitName: saved.name,
        initialCameraMode: 'me',
        getMyPlayerId: () => {
          const idx = myPlayerIndex();
          return idx >= 0 ? idx : null;
        },
        getActivePlayerId: () => currentState?.currentPlayerIndex ?? null,
        getTooltipHtml: (playerId) => boardTooltipHtml(playerId),
      });

      if (initial) renderHud();

      const syncPlayersFromState = (
        state: BoardStatePayload,
        previous: BoardStatePayload | null
      ) => {
        state.order.forEach((playerIndex) => {
          const nextPos = {
            mainPathIndex: state.playerPathIndices[playerIndex] ?? 0,
            jokerBranchIndex: null as number | null,
            startLaneIndex: state.playerStartLaneIndex?.[playerIndex] ?? null,
          };
          const prevLane = previous?.playerStartLaneIndex?.[playerIndex] ?? null;
          const prevPath = previous?.playerPathIndices[playerIndex];
          const changed =
            !previous || prevPath !== nextPos.mainPathIndex || prevLane !== nextPos.startLaneIndex;
          if (changed) {
            preview?.movePlayerTo?.(playerIndex, nextPos, previous ? 500 : 0);
          }
        });
      };

      teardownListeners.push(
        onBoardState((state) => {
          const previous = currentState;
          currentState = state;
          syncPlayersFromState(state, previous);
          renderHud();
        })
      );
      teardownListeners.push(
        onBoardWarmupEnd(() => {
          window.location.hash = buildCourseHash(meetingSession, driverId);
        })
      );
      teardownListeners.push(
        onBoardError((message) => {
          statusLine.textContent = message;
          // Plateau perdu (redémarrage serveur) : le flag « déjà joué » laisse
          // relancer la manche depuis Saisons sans rejouer le tour de chauffe.
          if (/indisponible|expiré/i.test(message)) {
            window.setTimeout(() => {
              if (location.hash.includes('plateau')) {
                window.location.hash = 'saisons';
              }
            }, 2200);
          }
        })
      );

      // Reload / nouvel onglet : le socket n’est plus mappé côté serveur —
      // sans reclaim, le HUD reste vide (pas de STATE) et les lancers échouent.
      emitBoardReclaim(meetingSession, driverId);
    })
    .catch((err: Error) => {
      section.innerHTML = `<p class="auth-error">${err.message}</p>`;
    });

  disposeCurrent = () => {
    teardownListeners.forEach((fn) => fn());
    teardownListeners.length = 0;
    preview?.dispose();
    preview = null;
  };
}

export function teardownBoardView(): void {
  disposeCurrent?.();
  disposeCurrent = null;
}
